import { useEffect, useMemo, useState } from 'react';

import { Link, useParams } from 'react-router-dom';

import { ProjectDetailSidebar } from '../components/projects/ProjectDetailSidebar';

import { ProjectFormModal } from '../components/projects/ProjectFormModal';

import { ProjectUpdateComposer } from '../components/projects/ProjectUpdateComposer';

import { ProjectUpdatesTimeline } from '../components/projects/ProjectUpdatesTimeline';

import { useAuth } from '../contexts/AuthContext';

import { useCrmData } from '../contexts/CrmDataContext';
import { usePortalPath } from '../contexts/PortalContext';
import { accountDetailPath } from '../lib/portal';

import { useMaxWidth } from '../hooks/useMaxWidth';

import { formatSupabaseError } from '../lib/formatSupabaseError';

import { changeProjectStatus } from '../lib/projectUpdates';

import { PROJECT_STATUSES } from '../types/crm';

import { clientById, idsMatch, userCanWriteOwnedRecord } from '../utils/crmHelpers';

import {

  projectByRef,

  updatesForProject,

  userCanAccessProject,

} from '../utils/projectHelpers';

import { ProjectReportExportButton } from '../components/projects/ProjectReportExportButton';

import {

  projectUpdateAuthorId,

  systemAuthorIdForLog,

} from '../utils/ownerId';



type ProjectTab = 'details' | 'timeline';



export function ProjectDetailPage() {

  const projectsPath = usePortalPath('projects');
  const { ref: refParam, id: idParam } = useParams<{ ref?: string; id?: string }>();

  const { profile } = useAuth();

  const {
    data,
    effectiveRole,
    username,
    refresh,
    ownerLabel,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  } = useCrmData();

  const isMobileLayout = useMaxWidth(900);

  const [centerTab, setCenterTab] = useState<ProjectTab>('timeline');

  const exportCtx = useMemo(

    () => ({ data, ownerLabel }),

    [data, ownerLabel],

  );



  const postAuthorId = useMemo(

    () => projectUpdateAuthorId(profile, data.users),

    [profile, data.users],

  );



  const systemLogAuthorId = useMemo(

    () => systemAuthorIdForLog(profile, data.users, data.updates),

    [profile, data.users, data.updates],

  );

  const [editOpen, setEditOpen] = useState(false);

  const [statusSaving, setStatusSaving] = useState(false);

  const [statusError, setStatusError] = useState<string | null>(null);



  const project = useMemo(() => {

    if (refParam) return projectByRef(data.projects, refParam);

    if (idParam) return data.projects.find((p) => p.id === idParam);

    return undefined;

  }, [data.projects, refParam, idParam]);



  useEffect(() => {

    setCenterTab('timeline');

  }, [project?.id, isMobileLayout]);



  useEffect(() => {

    if (!isMobileLayout && centerTab === 'details') {

      setCenterTab('timeline');

    }

  }, [isMobileLayout, centerTab]);



  const ownedAccess = {
    departmentSlug,
    clientId: project?.client_id,
    clients: data.clients,
    actorSalespersonCodes,
  };

  const canView = userCanAccessProject(
    project,
    effectiveRole,
    username,
    data.users,
    supervisorTeam,
    ownedAccess,
  );

  const client = clientById(data.clients, project?.client_id);

  const contact = data.contacts.find((c) => idsMatch(c.id, project?.contact_id));

  const canEdit =
    project != null &&
    userCanWriteOwnedRecord(
      project.owner_id,
      effectiveRole,
      username,
      data.users,
      supervisorTeam,
      ownedAccess,
    );

  const canPostUpdate = Boolean(project && canEdit);



  const timelineUpdates = useMemo(

    () => (project ? updatesForProject(data.updates, project.id) : []),

    [data.updates, project],

  );



  if (!project) {

    return (

      <>

        <p className="empty-state">Project not found.</p>

        <Link to={projectsPath}>← Back to projects</Link>

      </>

    );

  }



  if (!canView) {

    return (

      <>

        <Link to={projectsPath} className="back-link">

          ← Projects

        </Link>

        <div className="card access-denied-panel" style={{ marginTop: 24 }}>

          <h1 className="page-title" style={{ fontSize: '1.25rem' }}>

            Access restricted

          </h1>

          <p className="page-lead">

            You can see this project in the list, but full workspace access is limited to the project

            owner and elevated roles.

          </p>

          <Link to={projectsPath} className="btn-primary">

            Back to projects

          </Link>

        </div>

      </>

    );

  }



  async function handleStatusChange(next: string) {

    if (!project || next === project.status || statusSaving) return;

    setStatusSaving(true);

    setStatusError(null);

    try {

      await changeProjectStatus(project.id, next, systemLogAuthorId);

      await refresh();

    } catch (err) {

      setStatusError(formatSupabaseError(err));

    } finally {

      setStatusSaving(false);

    }

  }



  const showDetails = !isMobileLayout || centerTab === 'details';

  const showTimeline = !isMobileLayout || centerTab === 'timeline';



  return (

    <div className="project-detail-layout">

      <Link to={projectsPath} className="back-link">

        ← Projects

      </Link>



      <header className="detail-header project-detail-layout__header">

        <div className="detail-header__text">

          <p className="detail-eyebrow">Project</p>

          <h1 className="page-title">{project.title}</h1>

          <p className="page-lead">

            {project.reference_no ?? '—'}

            {client && (

              <>

                {' '}

                ·{' '}

                <Link to={accountDetailPath(client.code)}>{client.company_name}</Link>

              </>

            )}

          </p>

        </div>

        <div className="project-detail-header__actions">

          <ProjectReportExportButton project={project} ctx={exportCtx} />

          {canEdit && (

            <>

              <label className="field project-detail-status-field">

                <span className="field-label">Status</span>

                <select

                  className="field-input"

                  value={project.status ?? PROJECT_STATUSES[0]}

                  disabled={statusSaving}

                  onChange={(e) => void handleStatusChange(e.target.value)}

                >

                  {PROJECT_STATUSES.map((s) => (

                    <option key={s} value={s}>

                      {s}

                    </option>

                  ))}

                </select>

              </label>

              <button type="button" className="btn-primary" onClick={() => setEditOpen(true)}>

                Edit project

              </button>

            </>

          )}

          {!canEdit && (

            <span

              className={`project-status ${project.status === 'Completed' ? 'project-status--completed' : project.status === 'On Hold' ? 'project-status--hold' : 'project-status--ongoing'}`}

            >

              {project.status ?? 'Ongoing'}

            </span>

          )}

        </div>

      </header>



      {statusError && <div className="alert alert-error">{statusError}</div>}



      {isMobileLayout && (

        <header className="project-center-workspace__tabs" role="tablist" aria-label="Project sections">

          <button

            type="button"

            role="tab"

            aria-selected={centerTab === 'timeline'}

            className={`account-center-workspace__tab${centerTab === 'timeline' ? ' account-center-workspace__tab--active' : ''}`}

            onClick={() => setCenterTab('timeline')}

          >

            Timeline

            <span className="account-center-workspace__tab-count">{timelineUpdates.length}</span>

          </button>

          <button

            type="button"

            role="tab"

            aria-selected={centerTab === 'details'}

            className={`account-center-workspace__tab${centerTab === 'details' ? ' account-center-workspace__tab--active' : ''}`}

            onClick={() => setCenterTab('details')}

          >

            Details

          </button>

        </header>

      )}



      <div className="project-detail-page">

        {showDetails && (

          <ProjectDetailSidebar project={project} client={client} contact={contact} />

        )}



        {showTimeline && (

          <section className="project-chat-workspace" aria-label="Project timeline">

            <div className="project-chat-workspace__messages">

              <ProjectUpdatesTimeline

                updates={timelineUpdates}

                users={data.users}

                currentUsername={username}

              />

            </div>

            <div className="project-chat-workspace__composer">

              {canPostUpdate ? (

                <ProjectUpdateComposer

                  projectId={project.id}

                  authorId={postAuthorId}

                  allowAttachments={canEdit}

                  onPosted={() => void refresh()}

                />

              ) : (

                <p className="field-hint project-chat-workspace__readonly">

                  Only the project owner or elevated roles can post updates.

                </p>

              )}

            </div>

          </section>

        )}

      </div>



      <ProjectFormModal

        open={editOpen}

        project={project}

        defaultClientId={project.client_id}

        lockClient

        onClose={() => setEditOpen(false)}

      />

    </div>

  );

}


